import React, { useState, useRef, useEffect } from 'react';
import styles from './AddVideoChapter.module.css';
import Loading from '../../../common/HousGlass Loader/Loading';
import { useQueryClient } from 'react-query';
import { useVideos } from '../../../hooks/admin/useAdminVideoUpload';
import { useCreateChapter } from '../../../hooks/admin/useAdminChapters';

function AddVideoChapter(data) {
  const { videos, isLoading: videosLoading, error: videosError } = useVideos();
  const { createChapter, isLoading: createLoading, isError, error: createError } = useCreateChapter();
  const queryClient = useQueryClient();
  
  const [selectedVideo, setSelectedVideo] = useState('');
  const [chapterNumber, setChapterNumber] = useState(1);
  const [message, setMessage] = useState({ text: '', type: '' });
  
  // New state for searchable dropdown
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Filter videos based on search term
  const filteredVideos = videos 
    ? videos.filter(video => 
        video.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
        video.language.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  // Handle selecting a video from dropdown
  const handleSelect = (video) => {
    setSelectedVideo(video.id);
    setSearchTerm(video.title);
    setDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedVideo) {
      setMessage({ text: 'Please select a video', type: 'error' });
      return;
    }

    // Check if the selected video already has a series associated
    const videoWithSeries = videos.find(video => 
      video.id === selectedVideo && video.have_series && video.series_id !== data.data.id
    );
    
    if (videoWithSeries) {
      setMessage({ 
        text: 'This video is already part of another series', 
        type: 'error' 
      });
      return;
    }

    setMessage({ text: '', type: '' });

    // Create chapter data object
    const chapterData = {
      series_id: data.data.id,
      chapter_number: chapterNumber,
      video_id: selectedVideo,
      type: 'video'
    };

    createChapter(chapterData, {
      onSuccess: () => {
        setMessage({ text: 'Chapter added successfully!', type: 'success' });
        setSelectedVideo('');
        setSearchTerm('');
        setChapterNumber(prev => prev + 1); // Auto-increment for next chapter
        queryClient.invalidateQueries(['seriesChapters', data.data.id]);
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to add chapter';
        setMessage({ text: errorMessage, type: 'error' });
      }
    });
  };

  if (videosLoading) {
    return <div className={styles.loading}><Loading/></div>;
  }

  if (videosError) {
    return <div className={styles.error}>Error loading videos: {videosError.message}</div>;
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Add Video Chapter to "{data.data.title}"</h2>
      
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="chapterNumber" className={styles.label}>Chapter Number:</label>
          <input
            type="number"
            id="chapterNumber"
            value={chapterNumber}
            onChange={(e) => setChapterNumber(parseInt(e.target.value) || 1)}
            min="1"
            className={styles.input}
            required
          />
        </div>
        
        <div className={styles.formGroup} ref={dropdownRef}>
          <label className={styles.label}>Select Video:</label>
          <div
            className={styles.searchableSelect}
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <input
              type="text"
              placeholder="Search or select video..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setDropdownOpen(true);
              }}
              className={styles.input}
            />
            <span className={styles.dropdownArrow}>▼</span>
          </div>
          {dropdownOpen && (
            <ul className={styles.dropdownList}>
              {filteredVideos.length > 0 ? (
                filteredVideos.map((video) => (
                  <li
                    key={video.id}
                    className={styles.dropdownItem}
                    onClick={() => handleSelect(video)}
                  >
                    {video.title} ({video.language})
                  </li>
                ))
              ) : (
                <li className={styles.dropdownItem}>No matching videos</li>
              )}
            </ul>
          )}
        </div>
        
        {selectedVideo && videos && (
          <div className={styles.previewCard}>
            <h3 className={styles.previewTitle}>Selected Video Preview</h3>
            {videos.filter(video => video.id === selectedVideo).map(video => (
              <div key={video.id} className={styles.videoPreview}>
                <div className={styles.videoImage}>
                  <img 
                    src={video.mobile_image_url || '/placeholder-video.png'} 
                    alt={video.title}
                    className={styles.previewImg}
                  />
                </div>
                <div className={styles.videoDetails}>
                  <p><strong>Title:</strong> {video.title}</p>
                  <p><strong>Duration:</strong> {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}</p>
                  <p><strong>Language:</strong> {video.language}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {message.text && (
          <div className={`${styles.message} ${styles[message.type]}`}>
            {message.text}
          </div>
        )}
        
        <button 
          type="submit" 
          className={styles.submitButton}
          disabled={createLoading || !selectedVideo}
        >
          {createLoading ? 'Adding...' : 'Add Chapter'}
        </button>
      </form>
    </div>
  );
}

export default AddVideoChapter;